/**
 * DataAgentsPage Component
 *
 * Main page for managing data agents.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { DataAgentTable } from '../components/DataAgentTable';
import { CreateDataAgentModal } from '../components/CreateDataAgentModal';
import { UpdateDataAgentModal } from '../components/UpdateDataAgentModal';
import { DeleteDataAgentModal } from '../components/DeleteDataAgentModal';
import { DataAgentDetailsModal } from '../components/DataAgentDetailsModal';
import { GenerateTokenModal } from '../components/GenerateTokenModal';
import { useDataAgents } from '../hooks/useDataAgents';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { DataAgent, DataAgentStatus } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

export const DataAgentsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<DataAgent | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showTokenModal, setShowTokenModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data agents
  const {
    data: agentsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDataAgents(tenantId, {
    search: debouncedSearch || undefined,
    status: statusFilter as DataAgentStatus || undefined,
  });

  const dataAgents = agentsData || [];

  // Filter data agents locally
  const filteredAgents = useMemo(() => {
    if (!dataAgents) return [];

    let filtered = [...dataAgents];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (agent) =>
          agent.dataAgentName.toLowerCase().includes(query) ||
          agent.dataAgentCode.toLowerCase().includes(query) ||
          (agent.description && agent.description.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((agent) => agent.status === statusFilter);
    }

    return filtered;
  }, [dataAgents, debouncedSearch, statusFilter]);

  const handleViewDetails = (agent: DataAgent) => {
    setSelectedAgent(agent);
    setShowDetailsModal(true);
  };

  const handleEdit = (agent: DataAgent) => {
    setSelectedAgent(agent);
    setShowUpdateModal(true);
  };

  const handleDelete = (agent: DataAgent) => {
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  const handleGenerateToken = (agent: DataAgent) => {
    setSelectedAgent(agent);
    setShowTokenModal(true);
    // Close details modal if open
    setShowDetailsModal(false);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Data Agents</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage self-hosted agents that execute ELT jobs
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            Create Data Agent
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search data agents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="unknown">Unknown</option>
            </Select>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Data Agents Table */}
        <DataAgentTable
          dataAgents={filteredAgents}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onGenerateToken={handleGenerateToken}
        />

        {/* Modals */}
        <CreateDataAgentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedAgent && (
          <DataAgentDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedAgent(null);
            }}
            tenantId={tenantId}
            dataAgentId={selectedAgent.dataAgentId}
            onGenerateToken={handleGenerateToken}
          />
        )}

        {showUpdateModal && selectedAgent && (
          <UpdateDataAgentModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedAgent(null);
            }}
            tenantId={tenantId}
            dataAgent={selectedAgent}
          />
        )}

        {showDeleteModal && selectedAgent && (
          <DeleteDataAgentModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedAgent(null);
            }}
            dataAgent={selectedAgent}
          />
        )}

        {showTokenModal && selectedAgent && (
          <GenerateTokenModal
            isOpen={showTokenModal}
            onClose={() => {
              setShowTokenModal(false);
              setSelectedAgent(null);
            }}
            tenantId={tenantId}
            dataAgent={selectedAgent}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
